import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { navLinks, company } from "../mock";
import { Button } from "./ui/button";

const SCROLL_THRESHOLD_PX = 12;

const NavLogo = () => (
  <Link to="/" className="flex items-center gap-2.5 group">
    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-white font-display font-extrabold text-lg shadow-md shadow-primary/20 transition-transform group-hover:scale-105">
      O
    </span>
    <span className="font-display text-xl font-extrabold tracking-tight text-slate-900">
      Ozziee<span className="text-primary">Tech</span>
    </span>
  </Link>
);

const DesktopNav = ({ pathname }) => (
  <ul className="hidden lg:flex items-center gap-1">
    {navLinks.map((link) => {
      const active = pathname === link.path;
      return (
        <li key={link.path}>
          <Link
            to={link.path}
            className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              active
                ? "text-primary"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            {link.label}
            {active && (
              <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 rounded-full bg-primary" />
            )}
          </Link>
        </li>
      );
    })}
  </ul>
);

const DesktopActions = () => (
  <div className="hidden lg:flex items-center gap-3">
    <a
      href={`tel:${company.phoneRaw}`}
      className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-primary transition-colors"
    >
      <Phone className="h-4 w-4" />
      {company.phone}
    </a>
    <Button asChild className="rounded-lg bg-primary hover:bg-primary/90 shadow-md shadow-primary/20">
      <a href="#contact">Get in Touch</a>
    </Button>
  </div>
);

const MobileMenu = ({ open, pathname }) => (
  <div
    className={`lg:hidden overflow-hidden transition-all duration-300 bg-white border-b border-slate-100 ${
      open ? "max-h-[460px]" : "max-h-0"
    }`}
  >
    <ul className="container-wide py-4 flex flex-col gap-1">
      {navLinks.map((link) => (
        <li key={link.path}>
          <Link
            to={link.path}
            className={`block px-4 py-3 rounded-lg text-base font-medium ${
              pathname === link.path
                ? "bg-primary/10 text-primary"
                : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            {link.label}
          </Link>
        </li>
      ))}
      <li className="px-4 pt-3">
        <Button asChild className="w-full rounded-lg bg-primary hover:bg-primary/90">
          <a href="#contact">Get in Touch</a>
        </Button>
      </li>
    </ul>
  </div>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-md shadow-sm border-b border-slate-100"
          : "bg-transparent"
      }`}
    >
      <nav className="container-wide flex items-center justify-between h-[72px]">
        <NavLogo />
        <DesktopNav pathname={location.pathname} />
        <DesktopActions />
        <button
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>
      <MobileMenu open={open} pathname={location.pathname} />
    </header>
  );
};

export default Navbar;
